import { useEffect, useState } from "react";
import { api, money, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Sparkles, Star, Coffee, Gift, ChevronRight, Copy, Check } from "lucide-react";

/**
 * Phase 24a — LoyaltyWalletCard.
 * Shown on /account when the signed-in customer has a loyalty wallet for the
 * active tenant. Surfaces points balance, tier, punch progress and a
 * one-click "redeem N points → voucher code" action.
 */
export default function LoyaltyWalletCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/me/loyalty");
      setData(d);
    } catch (e) {
      // Silent fail — customer might be visiting a tenant with no program yet.
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const redeem = async () => {
    if (!data?.wallet?.redeemable_pts) return;
    setRedeeming(true);
    try {
      const { data: r } = await api.post("/me/loyalty/redeem", { points: data.wallet.redeemable_pts });
      setVoucher(r);
      toast.success(`$${r.discount_aud.toFixed(2)} voucher unlocked!`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not redeem.");
    } finally {
      setRedeeming(false);
    }
  };

  const copyCode = async () => {
    if (!voucher?.voucher_code) return;
    try {
      await navigator.clipboard.writeText(voucher.voucher_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* ignore */ }
  };

  if (loading || !data || !data.program?.is_active) return null;

  const wallet = data.wallet;
  const punch = (data.punch_cards || []).find((c) => c.is_active);

  return (
    <div className="rounded-3xl bg-gradient-to-br from-[#1A1410] via-[#2A1C18] to-[#3A2A24] text-white p-6 sm:p-7 shadow-[0_18px_50px_rgba(0,0,0,0.18)] border border-stone-900/30" data-testid="loyalty-wallet-card">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-[10px] tracking-[0.22em] uppercase font-medium">
            <Trophy className="w-3 h-3" /> {data.program.name}
          </div>
          <div className="mt-3 text-4xl font-serif tracking-tight" data-testid="loyalty-points-balance">
            {wallet.points_balance.toLocaleString()}
            <span className="text-base text-white/60 ml-2 font-sans">points</span>
          </div>
          <div className="text-xs text-white/60 mt-1">
            {wallet.points_earned_this_year.toLocaleString()} earned this year · lifetime {wallet.points_earned_lifetime.toLocaleString()}
          </div>
        </div>
        <div className="text-right shrink-0">
          <TierPill name={wallet.tier} multiplier={wallet.tier_multiplier} />
          {wallet.tier_perk && (
            <div className="text-[11px] text-white/55 mt-2 max-w-[180px] leading-snug italic">{wallet.tier_perk}</div>
          )}
        </div>
      </div>

      {/* Redemption strip */}
      <div className="border-t border-white/10 pt-4 mt-4">
        {voucher ? (
          <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="loyalty-redeem-result">
            <div className="flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-amber-300" />
              Voucher unlocked — <b className="font-medium">${voucher.discount_aud.toFixed(2)}</b> off your next order.
            </div>
            <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/30 text-emerald-100 rounded-full pl-4 pr-2 py-1.5 text-sm">
              <span>Code:</span><b className="font-mono tracking-wider">{voucher.voucher_code}</b>
              <button onClick={copyCode} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20" aria-label="Copy voucher code">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        ) : wallet.redeemable_pts > 0 ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-amber-300" />
              You can redeem <b className="font-medium">{wallet.redeemable_pts.toLocaleString()}</b> pts for <b className="font-medium">${wallet.redeemable_aud.toFixed(2)}</b> off your next order.
            </div>
            <button
              onClick={redeem}
              disabled={redeeming}
              data-testid="loyalty-redeem-btn"
              className="inline-flex items-center gap-1.5 bg-amber-300 text-stone-900 font-medium text-sm px-4 py-2 rounded-full hover:bg-amber-200 disabled:opacity-60 transition"
            >
              <Gift className="w-3.5 h-3.5" />
              {redeeming ? "Generating…" : `Redeem $${wallet.redeemable_aud.toFixed(2)}`}
            </button>
          </div>
        ) : (
          <div className="text-sm text-white/55 italic" data-testid="loyalty-no-redemption">
            Earn {Math.max(1, data.program.min_points_to_redeem - wallet.points_balance).toLocaleString()} more points to unlock your first reward.
          </div>
        )}
      </div>

      {/* Punch card (first active) */}
      {punch && (
        <PunchProgress card={punch} />
      )}

      {/* Tier ladder */}
      {data.tiers?.length > 1 && (
        <div className="mt-5 pt-4 border-t border-white/10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 mb-2">Tier ladder</div>
          <div className="flex gap-2 flex-wrap">
            {data.tiers.map((t) => (
              <div
                key={t.name}
                className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${
                  t.name === wallet.tier
                    ? "bg-amber-300/15 border-amber-300/40 text-amber-100"
                    : "border-white/10 text-white/40"
                }`}
                data-testid={`loyalty-tier-${t.name.toLowerCase()}`}
              >
                <Star className="w-3 h-3" /> {t.name} · x{t.multiplier}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TierPill({ name, multiplier }) {
  const tone = {
    Bronze: "bg-[#7A4B2A]/40 border-[#C28A55]/40 text-[#F0CBA0]",
    Silver: "bg-[#5A6273]/40 border-[#A6B3C9]/40 text-[#DDE3EE]",
    Gold:   "bg-[#7A5A22]/40 border-[#E4C26A]/50 text-[#FAE2A2]",
    VIP:    "bg-[#6B2F44]/40 border-[#D479A3]/40 text-[#FAC8DE]",
  }[name] || "bg-white/10 border-white/15 text-white/85";
  return (
    <div className={`inline-flex flex-col items-end gap-0.5 px-3 py-1.5 rounded-full border text-[11px] tracking-[0.18em] uppercase font-medium ${tone}`} data-testid="loyalty-tier-pill">
      <span className="leading-none">{name}</span>
      <span className="text-[9px] opacity-80 leading-none normal-case tracking-normal">x{multiplier} multiplier</span>
    </div>
  );
}

function PunchProgress({ card }) {
  const filled = card.count;
  const total = card.items_required;
  const slots = Array.from({ length: total }, (_, i) => i < filled);
  return (
    <div className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-4" data-testid="loyalty-punch-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Coffee className="w-4 h-4 text-amber-300" />
          <div className="text-sm font-medium">{card.name}</div>
        </div>
        <div className="text-[11px] text-white/55">Reward: {card.reward_label}</div>
      </div>
      <div className="flex gap-1.5 mb-2">
        {slots.map((on, i) => (
          <div
            key={i}
            className={`flex-1 h-2 rounded-full ${on ? "bg-amber-300" : "bg-white/10"}`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-[11px] text-white/55">
        <span>{filled} / {total} stamps</span>
        {card.rewards_available > 0 ? (
          <span className="text-emerald-300 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> {card.rewards_available} reward{card.rewards_available > 1 ? "s" : ""} ready!
          </span>
        ) : (
          <span className="inline-flex items-center gap-1">
            {card.next_reward_in} to go <ChevronRight className="w-3 h-3" />
          </span>
        )}
      </div>
    </div>
  );
}
